import React, { useEffect, useState } from "react";
import { clsx } from "clsx";
import { formatDistanceToNow, differenceInMinutes } from "date-fns";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";

import getRelativeTime from "~utils/getRelativeTime";

import styles from "./relative-timestamp.module.css";

export interface RelativeTimestampProps {
    date: string;
    prefix?: string;
    urgencyEnabled?: boolean;
    urgencyCutoffMins?: number;
    useShorthand?: boolean;
}

export const getTimeAgoShorthand = (date: Date) => {
    const { time, label } = getRelativeTime(date.toISOString());

    return `${time} ${label} ago`;
};

export const getTimeAgoLonghand = (date: Date) => {
    return formatDistanceToNow(date, {
        addSuffix: true,
    });
};

export function RelativeTimestamp({
    date,
    prefix,
    urgencyEnabled = true,
    urgencyCutoffMins = 30,
    useShorthand = false,
}: RelativeTimestampProps) {
    const [userDate, setUserDate] = useState<Date>(new Date(date));
    const [classNames, setClassNames] = useState(styles.relativeTimestamp);

    useEffect(() => {
        const now = new Date();
        const relativeDate = new Date(date);
        const dateDiff = differenceInMinutes(now, relativeDate);

        if (dateDiff < urgencyCutoffMins && urgencyEnabled) {
            setClassNames(
                clsx([
                    styles.relativeTimestamp,
                    styles.urgent,
                    typographyStyles.articleLabel,
                ])
            );
        }

        setUserDate(relativeDate);
    }, [date]);

    const timeAgo = useShorthand
        ? getTimeAgoShorthand(userDate)
        : getTimeAgoLonghand(userDate);

    return (
        <time
            dateTime={date}
            className={classNames}
            data-component="relative-timestamp"
            suppressHydrationWarning
        >
            {prefix}
            {timeAgo}
        </time>
    );
}
